import { useTheme } from "@mui/material";
import { useAnalytics } from "api/analytics";
import { LineChartOverview } from "components/GraphsSections/LineChartWithOverview/LineChartOverview";
import {
  currentDate,
  formatDate,
  formatDateToParse,
  getComparativeDate,
  getFormatByAggregationType,
  getFormattedSelectedDate,
  getStartDate,
  showComparativeData,
} from "components/GraphsSections/LineChartWithOverview/utils";
import { ReactComponent as ChartComparisonLine } from "components/Icons/chartComparisonLine.svg";
import { ReactComponent as ChartLine } from "components/Icons/chartLine.svg";
import { OLTheme } from "contexts/Theme/ThemeProvider";
import { useMessage } from "hooks/intl";
import { mapValues, toNumber } from "lodash";
import { useState } from "react";
import {
  AggregationType,
  ChartId,
  ChartInterval,
  ChartTab,
  LineChartData,
  UnitsWalletOverviewData,
  WalletMetrics,
  WalletMetricsUnion,
} from "types/charts";
import { BEWalletTypeFormat } from "types/wallets";
import { OLDate } from "utilities/dateFormat";
import { roundNumber } from "utilities/numbers";

const activeIntervalSettingsInitialValues: ChartInterval = {
  intervalLabel: "last30Days",
  aggregationType: AggregationType.DAY,
  intervalStartDate: getStartDate(currentDate, 30),
  intervalEndDate: formatDate(currentDate),
};

interface Props {
  walletDetails?: BEWalletTypeFormat;
}

export function WalletOverview({ walletDetails }: Props) {
  const { message } = useMessage();
  const theme: OLTheme = useTheme();
  const [activeTab, setActiveTab] = useState<WalletMetricsUnion>(
    WalletMetrics.UNITS_ISSUED,
  );
  const [activeIntervalSettings, setActiveIntervalSettings] = useState(
    activeIntervalSettingsInitialValues,
  );
  const { useUnitsOverviewAnalytics } = useAnalytics();
  const { data } = useUnitsOverviewAnalytics(walletDetails?.code as string, {
    filters: [
      {
        condition: "",
        query: "dataType",
        value: activeTab,
      },
      ...(activeIntervalSettings.aggregationType
        ? [
            {
              condition: "",
              query: "aggregationType",
              value: activeIntervalSettings.aggregationType,
            },
          ]
        : []),
      ...(activeIntervalSettings.intervalStartDate
        ? [
            {
              condition: "",
              query: "intervalStartDate",
              value: activeIntervalSettings.intervalStartDate,
            },
          ]
        : []),
      ...(activeIntervalSettings.intervalEndDate
        ? [
            {
              condition: "",
              query: "intervalEndDate",
              value: activeIntervalSettings.intervalEndDate,
            },
          ]
        : []),
    ],
  });

  const { data: comparativeData } = useUnitsOverviewAnalytics(
    walletDetails?.code as string,
    {
      filters: [
        {
          condition: "",
          query: "dataType",
          value: activeTab,
        },
        ...(activeIntervalSettings.aggregationType
          ? [
              {
                condition: "",
                query: "aggregationType",
                value: activeIntervalSettings.aggregationType,
              },
            ]
          : []),
        ...(activeIntervalSettings.intervalStartDate
          ? [
              {
                condition: "",
                query: "intervalStartDate",
                value: getComparativeDate(activeIntervalSettings, "start"),
              },
            ]
          : []),
        ...(activeIntervalSettings.intervalEndDate
          ? [
              {
                condition: "",
                query: "intervalEndDate",
                value: getComparativeDate(activeIntervalSettings, "end"),
              },
            ]
          : []),
      ],
    },
  );

  const percentageDataTypes = ["redemptionRate", "breakageRate"];

  const convertData = (data: UnitsWalletOverviewData) => {
    // For proper chart scaling, convert data of a specific type to percentage.
    return {
      ...data,
      data: mapValues(data?.data, (value) =>
        percentageDataTypes.includes(data.dataType)
          ? parseFloat(roundNumber(value * 100, 2))
          : value,
      ),
      header: mapValues(data?.header, (value, key) =>
        percentageDataTypes.includes(key)
          ? parseFloat(roundNumber(value * 100, 2))
          : value,
      ),
    };
  };

  const analyticsData: UnitsWalletOverviewData = convertData(data);
  const analyticsComparativeData: UnitsWalletOverviewData = showComparativeData(
    activeIntervalSettings.intervalLabel,
  )
    ? comparativeData && convertData(comparativeData)
    : [];

  const chartTabs: ChartTab[] = [
    {
      name: WalletMetrics.UNITS_ISSUED,
      tooltip: {
        externalLink: {
          path: "https://help.openloyalty.io/main-features/dashboard/units-wallet-overview",
        },
        text: message(
          { id: "issuedUnitsTooltip" },
          { units: walletDetails?.unitPluralName },
        ),
      },
      value: analyticsData?.header?.unitsIssued,
      formatNumber: true,
    },
    {
      name: WalletMetrics.UNITS_ACTIVE,
      tooltip: {
        externalLink: {
          path: "https://help.openloyalty.io/main-features/dashboard/units-wallet-overview",
        },
        text: message(
          { id: "unitsActiveTooltip" },
          { units: walletDetails?.unitPluralName },
        ),
      },
      value: analyticsData?.header?.unitsActive,
      formatNumber: true,
    },
    {
      name: WalletMetrics.UNITS_PENDING,
      tooltip: {
        externalLink: {
          path: "https://help.openloyalty.io/main-features/dashboard/units-wallet-overview",
        },
        text: message(
          { id: "pendingUnitsTooltip" },
          { units: walletDetails?.unitPluralName },
        ),
      },
      value: analyticsData?.header?.unitsPending,
      formatNumber: true,
    },
    {
      name: WalletMetrics.UNITS_SPENT,
      tooltip: {
        externalLink: {
          path: "https://help.openloyalty.io/main-features/dashboard/units-wallet-overview",
        },
        text: message(
          { id: "unitsSpentTooltip" },
          { units: walletDetails?.unitPluralName },
        ),
      },
      value: analyticsData?.header?.unitsSpent,
      formatNumber: true,
    },
    {
      name: WalletMetrics.UNITS_EXPIRED,
      tooltip: {
        externalLink: {
          path: "https://help.openloyalty.io/main-features/dashboard/units-wallet-overview",
        },
        text: message(
          { id: "unitsExpiredTooltip" },
          { units: walletDetails?.unitPluralName },
        ),
      },
      value: analyticsData?.header?.unitsExpired,
      formatNumber: true,
    },
    {
      name: WalletMetrics.REDEMPTION_RATE,
      tooltip: {
        externalLink: {
          path: "https://help.openloyalty.io/main-features/dashboard/units-wallet-overview",
        },
        text: message(
          { id: "redemptionRateTooltip" },
          { units: walletDetails?.unitPluralName },
        ),
      },
      value: analyticsData?.header?.redemptionRate,
      formatToPercentage: true,
    },
    {
      name: WalletMetrics.BREAKAGE_RATE,
      tooltip: {
        externalLink: {
          path: "https://help.openloyalty.io/main-features/dashboard/units-wallet-overview",
        },
        text: message(
          { id: "breakageRateTooltip" },
          { units: walletDetails?.unitPluralName },
        ),
      },
      value: analyticsData?.header?.breakageRate,
      formatToPercentage: true,
    },
  ];

  const { intervalLabel, intervalStartDate, intervalEndDate } =
    activeIntervalSettings;

  const isCustomDateRangeSelected = intervalLabel === "customDateRange";
  const formattedIntervalStartDate =
    getFormattedSelectedDate(intervalStartDate);
  const formattedIntervalEndDate = getFormattedSelectedDate(intervalEndDate);

  const lineChartData: LineChartData[] = analyticsData?.data && [
    {
      color: theme.primary500,
      data: Object.keys(analyticsData.data).map((key) => {
        const { aggregationType } = analyticsData;
        const dateToParse = formatDateToParse(key, aggregationType);

        return {
          x: Date.parse(dateToParse)
            ? OLDate(dateToParse, {
                formatString: getFormatByAggregationType(aggregationType),
              })
            : "",
          y: toNumber(analyticsData.data[key]),
          isPercentageValue: percentageDataTypes.includes(
            analyticsData.dataType,
          ),
        };
      }),
      id: ChartId.MAIN,
      legendIcon: <ChartLine />,
      legendLabel: !isCustomDateRangeSelected
        ? message({
            id: intervalLabel,
          })
        : `${formattedIntervalStartDate} - ${formattedIntervalEndDate}`,
    },
    ...(analyticsComparativeData?.data
      ? [
          {
            color: theme.primary500,
            data: Object.keys(analyticsData.data).map((key, index) => {
              const { aggregationType } = analyticsData;
              const dateToParse = formatDateToParse(key, aggregationType);
              const comparativeDateToParse = formatDateToParse(
                Object.keys(analyticsComparativeData?.data)[index],
                aggregationType,
              );
              const yValue = toNumber(
                Object.values(analyticsComparativeData?.data)[index],
              );

              return {
                x: Date.parse(dateToParse)
                  ? OLDate(dateToParse, {
                      formatString: getFormatByAggregationType(aggregationType),
                    })
                  : "",
                y: isNaN(yValue) ? 0 : yValue,
                comparativeDate: OLDate(comparativeDateToParse, {
                  formatString: getFormatByAggregationType(aggregationType),
                }),
              };
            }),
            id: ChartId.COMPARATIVE,
            legendIcon: <ChartComparisonLine />,
            legendLabel: message({ id: "comparisonPeriod" }),
          },
        ]
      : []),
  ];

  return (
    <LineChartOverview
      tabs={chartTabs}
      data={lineChartData}
      activeTab={activeTab}
      aggregationType={analyticsData?.aggregationType}
      activeIntervalSettings={activeIntervalSettings}
      setActiveIntervalSettings={setActiveIntervalSettings}
      setActiveTab={setActiveTab}
      enableDownload
    />
  );
}
